import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PageTitle from '../components/PageTitle';
import { Link, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';
import { gridSize } from '@atlaskit/theme';
import Heading from '@atlaskit/heading';

import WhiteboxPage from '../components/WhiteboxPage';
import TallButton from '../components/TallButton';
import { Flag, OtpCodeFieldError } from '../types';
import { PrivacyPolicyLink, UserNoticeLink } from '../components/links';
import { isMobileOidc } from '../utilities/oidc/oidc';
import messages from './StepUpStartPage.messages';
import EmailSent from '../components/EmailSent';
import OtpCodeField from '../components/OtpCodeField';
import ButtonLink from '../components/ButtonLink';
import { addFlag } from '../actions/flag-actions';
import {
  resendCode,
  sendCode,
  updateFromSessionStorage,
  verifyOtpCode,
} from '../actions/step-up-actions';
import Bull from '../components/BulletSeparator';
import {
  useAnalyticsClient,
  usePageViewedEvent,
} from '../utilities/analytics/analytics-web-client';
import { useAppDispatch, useAppSelector } from '../stores/redux.store';
import { useNavigateSoftOrHard } from '../utilities/hooks/useNavigateSoftOrHard';
import { fetchPageState } from '../utilities/hooks/useGlobalPageState';
import { AidFrontendFeatureGate } from '../providers/FeatureGates';
import { checkFeatureGateOnceInitialized } from '../utilities/feature-flags/feature-gate';
import { useOperationalEvent } from '../utilities/analytics/analytics-web-client';

export const pageId = 'stepUpOtpChallengeScreen';

const OTP_CODE_LENGTH = 8;

const Form = styled.form`
  margin: ${gridSize()}px 0 0;
`;

const ButtonContainer = styled.div`
  margin-top: ${gridSize()}px;
`;

const RequestPasscodeButtonContainer = styled.div`
  margin-top: ${3 * gridSize()}px;
`;

const SecondaryLinkWrapper = styled.div`
  margin-top: ${gridSize()}px;
`;

/**
 * This restores the state from session storage and saves it to session storage if it changes.
 */
function useSyncWithSessionStorage(
  sessionStorageKey: string,
  sessionStorageValue: string,
  onStorageUpdate: (value: string) => void
) {
  useEffect(() => {
    const storageListener = event => {
      if (event.key === sessionStorageKey) {
        if (event.newValue !== sessionStorageValue) {
          onStorageUpdate(event.newValue);
        }
      }
    };
    window.addEventListener('storage', storageListener);
    const sessionStorageItem = window.sessionStorage.getItem(sessionStorageKey);
    if (sessionStorageItem) {
      onStorageUpdate(sessionStorageItem);
    }
    return () => {
      window.removeEventListener('storage', storageListener);
    };
    // This code looks intended to only run on component mount. Missing deps assumed okay at the
    // time the linting rule was added, but may need further investigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (sessionStorageValue) {
      window.sessionStorage.setItem(sessionStorageKey, sessionStorageValue);
    }
  }, [sessionStorageValue, sessionStorageKey]);

  const clearSessionStorage = useCallback(() => {
    window.sessionStorage.removeItem(sessionStorageKey);
  }, [sessionStorageKey]);

  return [clearSessionStorage];
}

export async function stepUpStartLoader({ request }: { request: Request }) {
  if (!(await checkFeatureGateOnceInitialized(AidFrontendFeatureGate.USE_STATE_DATA_LOADERS))) {
    return null;
  }

  return await fetchPageState('/rest/step-up/start/state', request, {
    capabilities: ['capability-group--login', 'capability-name--mfa'],
  });
}

export const StepUpStartPage = () => {
  const analyticsClient = useAnalyticsClient();
  const intl = useIntl();

  const dispatch = useAppDispatch();
  const oidcContext = useAppSelector(state => state.microbranding.oidcContext);
  const isMobile = isMobileOidc(oidcContext);
  const email = useAppSelector(state => state.stepUp.email);
  const mfaToken = useAppSelector(state => state.stepUp.mfaToken);
  const dispatchAddFlag = useCallback(
    (flag: Flag) => dispatch(addFlag(flag.type, flag.title, flag.description)),
    [dispatch]
  );

  const [otpCode, setOtpCode] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [verificationError, setVerificationError] = useState<OtpCodeFieldError>();

  const { hash, search } = useLocation();
  const navigate = useNavigateSoftOrHard();
  const setHash = useCallback(
    (hash: string) => navigate(search + '#' + hash, { replace: true }),
    [navigate, search]
  );
  const [clearSessionStorageMfaToken] = useSyncWithSessionStorage(
    'mfaToken',
    mfaToken,
    mfaToken => {
      dispatch(updateFromSessionStorage(mfaToken));
    }
  );

  const [manualOtpCodeTriggerMode, setManualOtpCodeTriggerMode] = useState(() => {
    return document.visibilityState === 'hidden';
  });

  usePageViewedEvent(pageId, {
    value: manualOtpCodeTriggerMode ? 'manualOtpSend' : 'autoOtpSend',
  });
  useOperationalEvent({
    page: pageId,
    action: `loadStepUpStartPageSuccessful`,
    subject: 'sli',
  });

  const triggerCodeSend = useCallback(async () => {
    const codeSendResult = await dispatch(sendCode());
    if (codeSendResult.success) {
      analyticsClient.trackingEvent({
        page: pageId,
        subject: 'sendCode',
        action: 'succeed',
      });
      setHash('sent');
    } else if (codeSendResult.errorCode === 'too_many_requests') {
      dispatchAddFlag({
        type: 'error',
        title: messages.stepUpStartFlagErrorRateLimitTitle,
        description: messages.stepUpStartFlagErrorRateLimitDesc,
      });
      analyticsClient.errorShownEvent(pageId, 'sendCode', {
        errorCode: codeSendResult.errorCode,
      });
    } else {
      dispatchAddFlag({
        type: 'error',
        title: messages.stepUpStartFlagErrorTitle,
        description: messages.stepUpStartFlagErrorDesc,
      });
      analyticsClient.errorShownEvent(pageId, 'sendCode', {
        errorCode: codeSendResult.errorCode,
      });
    }
  }, [analyticsClient, dispatch, dispatchAddFlag, setHash]);

  useEffect(() => {
    if (hash === '#sent') {
      analyticsClient.trackingEvent({
        page: pageId,
        subject: 'sendCode',
        action: 'skipped',
        attributes: {
          reason: 'already sent',
        },
      });
    } else if (manualOtpCodeTriggerMode) {
      analyticsClient.trackingEvent({
        page: pageId,
        subject: 'sendCode',
        action: 'skipped',
        attributes: {
          reason: 'opened in background',
        },
      });
    } else {
      triggerCodeSend().catch(() =>
        dispatchAddFlag({
          type: 'error',
          title: messages.stepUpStartFlagErrorTitle,
          description: messages.stepUpStartFlagErrorDesc,
        })
      );
    }
    // We only want to ever trigger this effect once.
    // Otherwise we risk triggering multiple email code sends due to race conditions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      if (e) {
        e.preventDefault();
      }

      analyticsClient.uiEvent({
        page: pageId,
        subject: 'form',
        subjectId: 'otpForm',
        action: 'submitted',
      });

      setLoading(true);

      try {
        const result = await dispatch(verifyOtpCode(otpCode));
        if ('redirectUri' in result) {
          clearSessionStorageMfaToken();
          analyticsClient.trackingEvent({
            page: pageId,
            action: 'succeeded',
            subject: 'stepUpChallenge',
          });
          navigate(result.redirectUri);
        } else {
          analyticsClient.trackingEvent({
            page: pageId,
            action: 'failed',
            subject: 'stepUpChallenge',
            attributes: {
              errorCode: result.errorCode,
            },
          });
          setVerificationError(result);
        }
      } catch (e) {
        const errorCode = 'unknown';

        analyticsClient.trackingEvent({
          page: pageId,
          action: 'failed',
          subject: 'stepUpChallenge',
          attributes: {
            errorCode,
          },
        });
        setVerificationError({ errorCode });
      } finally {
        setLoading(false);
      }
    },
    [analyticsClient, dispatch, otpCode, clearSessionStorageMfaToken, navigate]
  );

  useEffect(() => {
    if (otpCode.length === OTP_CODE_LENGTH) {
      handleSubmit();
    }
  }, [handleSubmit, otpCode]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVerificationError(undefined);
    setOtpCode(e.target.value);
  };

  const handleResendCode = async () => {
    analyticsClient.linkClickedEvent(pageId, 'resendOtp');
    const result = await dispatch(mfaToken ? resendCode() : sendCode());
    if (result.success) {
      dispatchAddFlag({
        type: 'success',
        title: messages.stepUpStartFlagSuccessTitle,
        description: messages.stepUpStartFlagSuccessDesc,
      });
    } else {
      dispatchAddFlag({
        type: 'error',
        title: messages.stepUpStartFlagErrorTitle,
        description: messages.stepUpStartFlagErrorDesc,
      });
    }
  };

  const handleVerify = () => {
    analyticsClient.buttonClickedEvent(pageId, 'verifyOtp');
  };

  const handleReturnToLogin = () => {
    analyticsClient.linkClickedEvent(pageId, 'switchAccount');
  };

  const handleSendOtpCode = async () => {
    analyticsClient.buttonClickedEvent(pageId, 'sendOtpCode');
    await triggerCodeSend().catch(() =>
      dispatchAddFlag({
        type: 'error',
        title: messages.stepUpStartFlagErrorTitle,
        description: messages.stepUpStartFlagErrorDesc,
      })
    );
    setManualOtpCodeTriggerMode(false);
  };

  const returnLoginLink = `/login/select-account${search}`;

  return (
    <>
      <PageTitle title={intl.formatMessage(messages.stepUpStartTitle)} />
      <WhiteboxPage
        pageId={pageId}
        header={
          <Heading as="h1" size="small">
            <FormattedMessage
              {...(manualOtpCodeTriggerMode
                ? messages.stepUpOpenedInBackgroundHeader
                : messages.stepUpStartTitle)}
            />
          </Heading>
        }
        footer={
          <ul className="fine-print">
            <li>
              <PrivacyPolicyLink isMobileLink={isMobile} />
            </li>
            <Bull />
            <li>
              <UserNoticeLink isMobileLink={isMobile} />
            </li>
          </ul>
        }
        whiteboxFooter={
          <>
            {!manualOtpCodeTriggerMode && (
              <ButtonLink id="resend-code" spacing="none" onClick={handleResendCode}>
                <FormattedMessage {...messages.stepUpStartResendCode} />
              </ButtonLink>
            )}
            {!isMobile && (
              <SecondaryLinkWrapper>
                <Link
                  id="return-to-login"
                  data-testid="return-to-login"
                  to={returnLoginLink}
                  onClick={handleReturnToLogin}>
                  <FormattedMessage {...messages.stepUpStartReturnToLogin} />
                </Link>
              </SecondaryLinkWrapper>
            )}
          </>
        }>
        {!manualOtpCodeTriggerMode && (
          <div>
            <EmailSent
              email={email}
              message={intl.formatMessage(messages.stepUpStartEmailSentHeader)}
            />
            <Form autoComplete="off" onSubmit={handleSubmit}>
              <OtpCodeField
                id="otp-code-input"
                disabled={isLoading}
                fieldErrorFromParent={verificationError}
                onChange={handleInputChange}
                codeLength={OTP_CODE_LENGTH}
                codeType="numeric"
              />
              <ButtonContainer id="verify-submit">
                <TallButton
                  appearance="primary"
                  type="submit"
                  isFullWidth
                  isLoading={isLoading}
                  onClick={handleVerify}>
                  <FormattedMessage {...messages.stepUpStartVerify} />
                </TallButton>
              </ButtonContainer>
            </Form>
          </div>
        )}
        {manualOtpCodeTriggerMode && (
          <div>
            <EmailSent
              email={email}
              message={intl.formatMessage(messages.stepUpOpenedInBackgroundMessage)}
            />
            <RequestPasscodeButtonContainer id="send-otp-code">
              <TallButton
                appearance="primary"
                type="submit"
                isFullWidth
                onClick={handleSendOtpCode}>
                <FormattedMessage {...messages.stepUpStartSendCode} />
              </TallButton>
            </RequestPasscodeButtonContainer>
          </div>
        )}
      </WhiteboxPage>
    </>
  );
};

export default StepUpStartPage;
